import React from "react";
import { prepareLogin, register } from "../../api/auth";

export default function LoginForm({ onLogin, isSubmitting, errorMessage, logoUrl, onForgotPassword }) {
  const [step, setStep] = React.useState("email"); // "email" | "password" | "signup" | "setup_sent"
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [firstName, setFirstName] = React.useState("");
  const [lastName, setLastName] = React.useState("");
  const [isPreparing, setIsPreparing] = React.useState(false);
  const [isRegistering, setIsRegistering] = React.useState(false);
  const [prepareError, setPrepareError] = React.useState("");

  const handleEmailSubmit = async (event) => {
    event.preventDefault();
    setIsPreparing(true);
    setPrepareError("");

    try {
      const result = await prepareLogin(email.trim());
      if (result.status === "setup_sent") {
        setStep("setup_sent");
      } else {
        setStep("password");
      }
    } catch (error) {
      if (error?.status === 404) {
        setStep("signup");
      } else {
        setPrepareError("Unable to continue. Please try again.");
      }
    } finally {
      setIsPreparing(false);
    }
  };

  const handleSignupSubmit = async (event) => {
    event.preventDefault();
    setIsRegistering(true);
    setPrepareError("");

    try {
      await register(email.trim(), firstName.trim(), lastName.trim());
      setStep("setup_sent");
    } catch (error) {
      setPrepareError(
        error?.status === 409
          ? "An account with that email address already exists."
          : "Unable to create your account. Please try again."
      );
    } finally {
      setIsRegistering(false);
    }
  };

  const handlePasswordSubmit = async (event) => {
    event.preventDefault();
    await onLogin({ username: email.trim(), password });
  };

  const handleBack = () => {
    setStep("email");
    setPassword("");
    setFirstName("");
    setLastName("");
    setPrepareError("");
  };

  return (
    <div className="auth-shell">
      <section className="auth-card" aria-labelledby="login-title">
        <div className="auth-branding">
          <img className="auth-logo" src={logoUrl} alt="Meditec Staffing Company" />
          <h1 id="login-title">Timecard portal</h1>
        </div>

        {step === "email" && (
          <form className="auth-form" onSubmit={handleEmailSubmit}>
            <p className="auth-help-text">Enter your email address to continue.</p>

            <label className="field" htmlFor="login-email">
              <span>Email Address</span>
              <input
                id="login-email"
                type="text"
                autoComplete="username"
                inputMode="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                disabled={isPreparing}
                autoFocus
              />
            </label>

            <button type="submit" className="primary-action auth-submit" disabled={isPreparing || !email.trim()}>
              {isPreparing ? "Checking..." : "Continue"}
            </button>

            {prepareError ? (
              <div className="status-message error" role="alert">
                {prepareError}
              </div>
            ) : null}
          </form>
        )}

        {step === "password" && (
          <form className="auth-form" onSubmit={handlePasswordSubmit}>
            <div className="auth-email-display">
              <span>{email}</span>
              <button type="button" className="auth-change-email" onClick={handleBack}>
                Change
              </button>
            </div>

            <label className="field" htmlFor="password">
              <span>Password</span>
              <input
                id="password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                disabled={isSubmitting}
                autoFocus
              />
            </label>

            <button type="submit" className="primary-action auth-submit" disabled={isSubmitting || !password}>
              {isSubmitting ? "Signing in..." : "Sign in"}
            </button>

            <button type="button" className="auth-back-link" onClick={onForgotPassword} disabled={isSubmitting}>
              Forgot your password?
            </button>

            {errorMessage ? (
              <div className="status-message error" role="alert">
                {errorMessage}
              </div>
            ) : null}
          </form>
        )}

        {step === "signup" && (
          <form className="auth-form" onSubmit={handleSignupSubmit}>
            <p className="auth-help-text">No account found. Enter your name to create one.</p>

            <div className="auth-email-display">
              <span>{email}</span>
              <button type="button" className="auth-change-email" onClick={handleBack}>
                Change
              </button>
            </div>

            <label className="field" htmlFor="signup-first-name">
              <span>First name</span>
              <input
                id="signup-first-name"
                type="text"
                autoComplete="given-name"
                value={firstName}
                onChange={(event) => setFirstName(event.target.value)}
                disabled={isRegistering}
                autoFocus
              />
            </label>

            <label className="field" htmlFor="signup-last-name">
              <span>Last name</span>
              <input
                id="signup-last-name"
                type="text"
                autoComplete="family-name"
                value={lastName}
                onChange={(event) => setLastName(event.target.value)}
                disabled={isRegistering}
              />
            </label>

            <button
              type="submit"
              className="primary-action auth-submit"
              disabled={isRegistering || !firstName.trim() || !lastName.trim()}
            >
              {isRegistering ? "Creating account..." : "Create account"}
            </button>

            {prepareError ? (
              <div className="status-message error" role="alert">
                {prepareError}
              </div>
            ) : null}
          </form>
        )}

        {step === "setup_sent" && (
          <div className="auth-confirm" role="status">
            <div className="auth-confirm-icon" aria-hidden="true">✓</div>
            <p className="auth-confirm-title">Check your email</p>
            <p className="auth-confirm-body">We sent a password setup link to:</p>
            <p className="auth-confirm-email">{email}</p>
            <p className="auth-confirm-body">Open the email and follow the link to create your password.</p>
            <p className="auth-confirm-hint">Didn&rsquo;t receive it? Check your spam folder or wait a few minutes.</p>
            <button type="button" className="auth-back-link" onClick={handleBack}>
              Back to sign in
            </button>
          </div>
        )}

      </section>
    </div>
  );
}
